/**
 * Служебные экраны админа: упавшие задачи, журнал действий, отчёты сверок.
 *
 * «Лид не уехал в Битрикс» — это деньги, поэтому упавшая задача лежит строкой
 * в БД и повторяется кнопкой, а не теряется вместе с DLQ в Redis (§8.2).
 *
 * Журнал действий отдаётся с редакцией: в before/after попадают снимки
 * сущностей, а среди их полей встречаются хеши и секреты — наружу они не
 * уходят даже админу (§9.4, §9.5).
 */
import { z } from 'zod';
import { bigIdSchema, businessDateSchema, callDispositionSchema, isoDateTimeSchema, pageResponseSchema, phoneE164Schema, } from './common.dto.js';
/** Снимок сущности в аудите: произвольный JSON, значения секретов замаскированы */
export const auditSnapshotSchema = z.record(z.string(), z.unknown()).nullable();
export const jobFailureRowSchema = z.object({
    id: bigIdSchema,
    queue: z.string().nullable(),
    jobName: z.string().nullable(),
    jobId: z.string().nullable(),
    /** Текст ошибки без stack: путь к файлу на сервере в интерфейсе не нужен */
    error: z.string().nullable(),
    attempts: z.number().int().min(0),
    /** В payload задач лежат только идентификаторы и контекст запроса (§8.2) */
    payload: z.record(z.string(), z.unknown()).nullable(),
    failedAt: isoDateTimeSchema,
    resolvedAt: isoDateTimeSchema.nullable(),
    /** Строка, повтор которой породил эту: цепочка ретраев одной задачи */
    retriedFrom: bigIdSchema.nullable(),
});
export const jobFailuresQuerySchema = z.object({
    /** По умолчанию показываем неразобранные: разобранные — это архив */
    resolved: z.enum(['true', 'false', 'all']).default('false'),
    queue: z.string().max(60).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const jobFailuresResponseSchema = pageResponseSchema(jobFailureRowSchema);
/** POST /api/admin/job-failures/:id/retry — задача уходит обратно в свою очередь */
export const retryJobFailureResponseSchema = z.object({
    id: bigIdSchema,
    queue: z.string(),
    jobName: z.string(),
});
export const auditRowSchema = z.object({
    id: bigIdSchema,
    at: isoDateTimeSchema,
    actorUserId: bigIdSchema.nullable(),
    actorName: z.string().nullable(),
    actorIp: z.string().nullable(),
    requestId: z.string().nullable(),
    /** Машинное имя действия: 'assignment.label', 'settings.update', 'user.schedule.put' */
    action: z.string(),
    entityType: z.string().nullable(),
    entityId: bigIdSchema.nullable(),
    before: auditSnapshotSchema,
    after: auditSnapshotSchema,
});
export const auditQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
    actorUserId: bigIdSchema.optional(),
    action: z.string().max(80).optional(),
    entityType: z.string().max(40).optional(),
    entityId: bigIdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const auditResponseSchema = pageResponseSchema(auditRowSchema);
export const reconcileRowSchema = z.object({
    id: bigIdSchema,
    runAt: isoDateTimeSchema,
    /** 'dispatcher' | 'source' — какую витрину сверяли */
    scope: z.string().nullable(),
    businessDate: businessDateSchema.nullable(),
    /** Ключ строки витрины: идентификатор диспетчера или источника */
    key: z.string().nullable(),
    field: z.string().nullable(),
    stored: z.number().nullable(),
    recomputed: z.number().nullable(),
    fixed: z.boolean(),
});
export const reconcileQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
    scope: z.string().max(40).optional(),
    /** По умолчанию только расхождения: совпавшие строки — это шум на тысячи строк */
    onlyMismatched: z.enum(['true', 'false']).default('true'),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
export const reconcileResponseSchema = pageResponseSchema(reconcileRowSchema);
export const callMismatchRowSchema = z.object({
    callId: bigIdSchema,
    linkedid: z.string(),
    startedAt: isoDateTimeSchema,
    businessDate: businessDateSchema,
    phone: phoneE164Schema.nullable(),
    assignmentId: bigIdSchema.nullable(),
    dispatcherId: bigIdSchema.nullable(),
    dispatcherName: z.string().nullable(),
    /** 'ari' | 'cdr' | 'cdr_guess' — откуда взялись цифры звонка (§7.3) */
    sourceOfTruth: z.string(),
    disposition: callDispositionSchema,
    talkSec: z.number().int().min(0),
    isReached: z.boolean(),
});
export const callMismatchesQuerySchema = z.object({
    from: businessDateSchema,
    to: businessDateSchema,
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const callMismatchesResponseSchema = pageResponseSchema(callMismatchRowSchema);
//# sourceMappingURL=admin-ops.dto.js.map